import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import AdmittedPatientsModal from "../admission/AdmittedPatientsModal";
import CommonButton from "../../common11/Buttons/CommonButton";
import SearchDropdown from "../../common11/formInputs/SearchDropdown";
import {
  getAdmittedPatientSearchOptions,
  mapAdmissionRowToPatient,
} from "./ipdPatientUtils";

export function useIpdPatientSelection() {
  const [patient, setPatient] = useState(null);
  const { control, setValue } = useForm({
    defaultValues: { patientSearch: null },
  });

  const patientSearchOptions = useMemo(() => getAdmittedPatientSearchOptions(), []);

  const handleSelectPatient = (row) => {
    if (!row) {
      setPatient(null);
      setValue("patientSearch", null);
      return;
    }
    const mapped = mapAdmissionRowToPatient(row);
    setPatient(mapped);
    setValue("patientSearch", {
      id: row.admissionId || row.uhid,
      label: row.patientName || "",
      value: row.contact || "",
    });
  };

  const handlePatientSearchSelect = (selectedOption) => {
    if (!selectedOption) {
      handleSelectPatient(null);
      return;
    }
    if (selectedOption.row) {
      handleSelectPatient(selectedOption.row);
    }
  };

  return {
    patient,
    control,
    patientSearchOptions,
    handleSelectPatient,
    handlePatientSearchSelect,
  };
}

const IpdPatientInfoCard = ({ patient, variant = "grid" }) => {
  if (!patient) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        Search and select a patient to continue.
      </div>
    );
  }

  const patientName = patient.patientName || "—";
  const contact = patient.contact || "—";
  const uhid = patient.uhid || "—";
  const genderAge =
    patient.patientGender && patient.patientAge
      ? `${patient.patientGender} | ${patient.patientAge}`
      : "—";
  const doctorName = patient.doctorName || "—";
  const currentBed =
    [patient.wardName, patient.bedNo].filter(Boolean).join(" - ") || "—";

  if (variant === "sidebar") {
    const rows = [
      {
        label: "Patient Name",
        value: contact !== "—" ? `${patientName} (${contact})` : patientName,
      },
      { label: "Gender", value: patient.patientGender || "—" },
      { label: "Age", value: patient.patientAge || "—" },
      { label: "UHID", value: uhid },
      { label: "IPD No", value: patient.ipdNo || "—" },
      { label: "Date Of Admission", value: patient.admDateTime || "—" },
      { label: "Ward Name", value: patient.wardName || "—" },
      { label: "Bed No.", value: patient.bedNo || "—" },
      { label: "Consultant", value: doctorName },
    ];

    return (
      <div className="border border-gray-300 rounded bg-white h-full">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[9rem_1fr] gap-2 px-3 py-2 border-b border-gray-200 text-xs last:border-b-0"
          >
            <span className="font-semibold text-gray-700">{row.label}</span>
            <span className="text-gray-800">{row.value}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg p-3 border border-gray-200 shadow-sm">
      <div className="flex items-center gap-6">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
            <span className="text-lg font-semibold text-gray-600">
              {patientName !== "—" ? patientName.charAt(0).toUpperCase() : "?"}
            </span>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-3 text-sm">
          <div className="flex items-baseline gap-2 col-span-1">
            <span className="text-[#217E90] font-semibold">{patientName}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">UHID</span>
            <span className="text-[#217E90] font-semibold">: {uhid}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Gender / Age</span>
            <span className="text-[#217E90] font-semibold">: {genderAge}</span>
          </div>
          <div className="flex items-baseline gap-2 col-span-1">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Contact</span>
            <span className="text-[#217E90] font-semibold">: {contact}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Doctor name</span>
            <span className="text-[#217E90] font-semibold">: {doctorName}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-gray-500 text-xs min-w-[5.5rem] shrink-0">Current bed</span>
            <span className="text-[#217E90] font-semibold">: {currentBed}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

function IpdPatientSearchPanel({
  patient,
  control,
  patientSearchOptions,
  onPatientSearchSelect,
  onSelectPatient,
  showPatientCard = true,
  patientCardVariant = "grid",
}) {
  const [admittedModalOpen, setAdmittedModalOpen] = useState(false);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div className="w-full sm:flex-1 sm:max-w-2xl">
          <SearchDropdown
            name="patientSearch"
            control={control}
            dataArray={patientSearchOptions}
            onChange={onPatientSearchSelect}
            label="Search by patient name or mobile number"
            isSearchable
            isClearable
            fontSize="text-xs sm:text-sm"
            isFloatingLabel
          />
        </div>
        <div className="flex-shrink-0">
          <CommonButton
            label="Admitted Patients"
            type="button"
            size="sm"
            className="bg-[#217E90] text-white hover:bg-[#1a5e70] px-3 py-1.5 text-xs sm:text-sm w-full sm:w-auto"
            onClick={() => setAdmittedModalOpen(true)}
          />
        </div>
      </div>

      {showPatientCard && (
        <IpdPatientInfoCard patient={patient} variant={patientCardVariant} />
      )}

      <AdmittedPatientsModal
        isOpen={admittedModalOpen}
        onClose={() => setAdmittedModalOpen(false)}
        onSelectPatient={onSelectPatient}
        onRowDoubleClick={onSelectPatient}
      />
    </>
  );
}

export { IpdPatientInfoCard };
export default IpdPatientSearchPanel;
